import { Alert, Anchor, Text } from '@zillow/constellation';
import type { LogData } from '@zg-rentals/applications-reports';
import type { BackgroundReport as BackgroundReportType } from '@zg-rentals/ts-capps-client';
import { BackgroundReport as BackgroundCheck, LogLevel, UserType } from '@zg-rentals/applications-reports';
import { APPLICATIONS_FAQ_URL } from '../../../constants/urls';
import { bootstrap } from '../../../bootstrap';
import { getGlobalLogger } from '@zg-rentals/logger-browser';
import useGetUser from '../../../hooks/useGetUser';
import { useIsInMobileWebView } from '../../../hooks/useIsInMobileWebView';
import { trackRentalApplicationEvent } from '../../../analytics';
import { getRentalApplication } from '../../../analytics/getRentalApplication';
import { useApplicationContextOrUndefined } from '../../../contexts/ApplicationContext';

const logger = getGlobalLogger('applications-renter-web-background-report');

export function BackgroundReport({
  backgroundReport,
  zipCode,
  requestedDateTime,
  expirationDateTime,
  requiresConditionalApproval,
}: {
  backgroundReport?: BackgroundReportType;
  zipCode: string;
  requestedDateTime: string;
  expirationDateTime: string;
  requiresConditionalApproval: boolean;
}) {
  const isInMobileWebview = useIsInMobileWebView();
  const applicationContext = useApplicationContextOrUndefined();

  const { data: userData } = useGetUser();

  function log(logLevel: LogLevel, logData: LogData) {
    const userToken = userData?.userToken;
    const { message, error } = logData;
    switch (logLevel) {
      case LogLevel.Error:
        logger.error({ userId: userToken }, message, error);
        break;
      case LogLevel.Warn:
        logger.warn({ userId: userToken }, message, error);
        break;
      case LogLevel.Info:
        logger.info({ userId: userToken }, message, error);
        break;
      default:
        logger.warn({ userId: userToken }, message, error);
        break;
    }
  }

  return backgroundReport ? (
    <BackgroundCheck
      userType={UserType.Renter}
      backgroundReport={backgroundReport}
      zipCode={zipCode}
      requestedDateTime={requestedDateTime}
      expirationDateTime={expirationDateTime}
      isInMobileWebView={isInMobileWebview}
      requiresConditionalApproval={requiresConditionalApproval}
      conditionallyApprove={undefined}
      isConditionallyApproving={false}
      trackEvent={(clickstreamEvent) => {
        trackRentalApplicationEvent({
          ...clickstreamEvent,
          ...getRentalApplication(applicationContext),
        });
      }}
      emitMetric={(metricName) => bootstrap.monitor.count({ name: metricName })}
      log={log}
    />
  ) : (
    <Alert
      appearance="warning"
      body={
        <Text>
          Report not available. Contact{' '}
          <Anchor href={APPLICATIONS_FAQ_URL} target="_blank" rel="noopener noreferrer">
            customer support
          </Anchor>{' '}
          to report the issue. You can submit your application without the reports.
        </Text>
      }
    />
  );
}
